import React, { useEffect, useState } from 'react';
import { CheckCircle2Icon, PhoneIcon, MessageCircleIcon, AlertCircleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { business } from '../data/business';
import { membershipCopy } from '../data/membership';
import { SELECT_SERVICE_EVENT } from '../utils/booking';
type Status = 'idle' | 'submitting' | 'success';
const fieldClass = 'mt-1.5 w-full rounded-lg border border-navy/15 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 transition-colors duration-150 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20';
const labelClass = 'block text-xs font-semibold uppercase tracking-wide text-navy';
export function BookingForm() {
  const {
    t,
    lang
  } = useLanguage();
  const membership = membershipCopy[lang];
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    vehicle: '',
    date: '',
    time: '',
    message: '',
    callback: false
  });
  const update = (key: keyof typeof form, value: string | boolean) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  useEffect(() => {
    const onSelect = (event: Event) => {
      const option = (event as CustomEvent<string>).detail;
      setStatus('idle');
      setForm(prev => ({
        ...prev,
        service: option
      }));
    };
    window.addEventListener(SELECT_SERVICE_EVENT, onSelect);
    return () => window.removeEventListener(SELECT_SERVICE_EVENT, onSelect);
  }, []);
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setError(t.booking.errorRequired);
      return;
    }
    setError(null);
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 700);
  };
  const reset = () => {
    setForm({
      name: '',
      phone: '',
      email: '',
      service: '',
      vehicle: '',
      date: '',
      time: '',
      message: '',
      callback: false
    });
    setStatus('idle');
  };
  return <section id="contact" className="bg-navy py-20 sm:py-24" data-id="element-51" data-mp="3v6tavf5cxur2">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16 lg:px-8" data-id="element-52" data-mp="3giyi5yrcnqdz">
        <div data-id="element-53" data-mp="3lghugt4hlt3a">
          <p className="text-sm font-semibold text-brand-cyan" data-id="element-54" data-mp="3lg8c9rpooq85">{t.booking.kicker}</p>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl" data-id="element-55" data-mp="37gp634gfnoyh">
            {t.booking.title}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-white/70" data-id="element-56" data-mp="3bh0r4icpjrah">{t.booking.subtitle}</p>

          <p className="mt-10 text-sm font-medium text-white/60" data-id="element-57" data-mp="2xloto3763mpv">{t.booking.orCall}</p>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row lg:flex-col" data-id="element-58" data-mp="32ixg4fzcmcwx">
            <a href={business.phoneHref} className="inline-flex items-center gap-3 rounded-lg border border-white/20 px-4 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-brand-cyan hover:text-brand-cyan" data-id="element-59" data-mp="vtiu3bjimfs1">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-60" data-mp="2gb1jlyuz4i63" />
              {business.phoneDisplay}
            </a>
            <a href={business.whatsappHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 rounded-lg border border-white/20 px-4 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-brand-cyan hover:text-brand-cyan" data-id="element-61" data-mp="2uzxv65w8odbg">
              <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-62" data-mp="3dy0fi7dudm2x" />
              WhatsApp · {business.phoneDisplay}
            </a>
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-lift sm:p-8" data-id="element-63" data-mp="38zp6fyvdfflr">
          {status === 'success' ? <div className="flex h-full min-h-[420px] flex-col items-center justify-center text-center" data-id="element-64" data-mp="1jm3kk4g6xy34">
              <CheckCircle2Icon className="h-12 w-12 text-brand-blue" aria-hidden="true" data-id="element-65" data-mp="1emz9t9dmbsao" />
              <h3 className="mt-4 font-display text-2xl font-bold text-navy" data-id="element-66" data-mp="5q291yd5ccks">
                {t.booking.successTitle}
              </h3>
              <p className="mt-2 max-w-sm text-sm leading-relaxed text-ink/70" data-id="element-67" data-mp="fmf3wzkfgwh4">
                {t.booking.successBody}
              </p>
              <button type="button" onClick={reset} className="mt-6 rounded-lg border border-navy/15 px-4 py-2.5 text-sm font-semibold text-navy transition-colors duration-150 hover:border-brand-blue hover:text-brand-blue" data-id="element-68" data-mp="jmfnszqapzvk">
                {t.booking.successReset}
              </button>
            </div> : <form onSubmit={onSubmit} noValidate data-id="element-69" data-mp="pwo111dtobo">
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-70" data-mp="qppbmhknlmf">
                <div data-id="element-71" data-mp="30bl9ek9jr27e">
                  <label className={labelClass} htmlFor="name" data-id="element-72" data-mp="30c4hl99eac88">
                    {t.booking.fields.name} *
                  </label>
                  <input id="name" required value={form.name} onChange={e => update('name', e.target.value)} placeholder={t.booking.placeholders.name} className={fieldClass} data-id="element-73" data-mp="2wu6xfkfuowqu" />
                </div>
                <div data-id="element-74" data-mp="2itw7qkqa1o79">
                  <label className={labelClass} htmlFor="phone" data-id="element-75" data-mp="2iue89tjz2okz">
                    {t.booking.fields.phone} *
                  </label>
                  <input id="phone" type="tel" required value={form.phone} onChange={e => update('phone', e.target.value)} placeholder={t.booking.placeholders.phone} className={fieldClass} data-id="element-76" data-mp="2954i4j8ovfzi" />
                </div>
                <div data-id="element-77" data-mp="1v6xstrht2obg">
                  <label className={labelClass} htmlFor="email" data-id="element-78" data-mp="1q6a5bfsscplz">
                    {t.booking.fields.email}{' '}
                    <span className="font-normal normal-case text-ink/50" data-id="element-79" data-mp="1gi4tbisrzwa2">
                      ({t.booking.fields.optional})
                    </span>
                  </label>
                  <input id="email" type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder={t.booking.placeholders.email} className={fieldClass} data-id="element-80" data-mp="16kglbr65ss9y" />
                </div>
                <div data-id="element-81" data-mp="246ns97pxqwph">
                  <label className={labelClass} htmlFor="service" data-id="element-82" data-mp="24a1soaf1gvua">
                    {t.booking.fields.service}
                  </label>
                  <select id="service" value={form.service} onChange={e => update('service', e.target.value)} className={fieldClass} data-id="element-83" data-mp="1uhno79wktb4f">
                    <option value="" data-id="element-84" data-mp="2mz8cy1vhdvi7">{t.booking.selectService}</option>
                    <optgroup label={t.services.kicker} data-id="element-85" data-mp="bips8qeqaff3">
                      {t.booking.serviceOptions.map(option => <option key={option} value={option} data-id="element-86" data-mp="6jlgpt4c05ll">
                          {option}
                        </option>)}
                    </optgroup>
                    <optgroup label={membership.kicker} data-id="element-87" data-mp="gb949ixrp2hs">
                      {membership.plans.map(plan => <option key={plan.id} value={plan.optionLabel} data-id="element-88" data-mp="3oqsxwj2comxd">
                          {plan.optionLabel}
                        </option>)}
                    </optgroup>
                  </select>
                </div>
                <div className="sm:col-span-2" data-id="element-89" data-mp="3tpchicth1mmk">
                  <label className={labelClass} htmlFor="vehicle" data-id="element-90" data-mp="3tnj9ou74gwcz">
                    {t.booking.fields.vehicle}{' '}
                    <span className="font-normal normal-case text-ink/50" data-id="element-91" data-mp="1d2jt2cojv18d">
                      ({t.booking.fields.optional})
                    </span>
                  </label>
                  <input id="vehicle" value={form.vehicle} onChange={e => update('vehicle', e.target.value)} placeholder={t.booking.placeholders.vehicle} className={fieldClass} data-id="element-92" data-mp="32j5jq9cvapp4" />
                </div>
                <div data-id="element-93" data-mp="3h8l7rhl0kh14">
                  <label className={labelClass} htmlFor="date" data-id="element-94" data-mp="3l83t2egqyu4h">
                    {t.booking.fields.date}
                  </label>
                  <input id="date" type="date" value={form.date} onChange={e => update('date', e.target.value)} className={fieldClass} data-id="element-95" data-mp="3q65cj0i34vgh" />
                </div>
                <div data-id="element-96" data-mp="20ss3ixz12j91">
                  <label className={labelClass} htmlFor="time" data-id="element-97" data-mp="20ta76fo0rzqz">
                    {t.booking.fields.time}
                  </label>
                  <input id="time" type="time" value={form.time} onChange={e => update('time', e.target.value)} className={fieldClass} data-id="element-98" data-mp="25tfs7czsao03" />
                </div>
                <div className="sm:col-span-2" data-id="element-99" data-mp="e4oqsre3bdfd">
                  <label className={labelClass} htmlFor="message" data-id="element-100" data-mp="94sm0pxkjhyr">
                    {t.booking.fields.message}{' '}
                    <span className="font-normal normal-case text-ink/50" data-id="element-101" data-mp="15vct46ri2oum">
                      ({t.booking.fields.optional})
                    </span>
                  </label>
                  <textarea id="message" rows={3} value={form.message} onChange={e => update('message', e.target.value)} placeholder={t.booking.placeholders.message} className={fieldClass} data-id="element-102" data-mp="wu4v7pswbq12" />
                </div>
              </div>

              <label className="mt-5 flex items-center gap-3 text-sm text-ink/75" data-id="element-103" data-mp="3cnlyt0iiw2rv">
                <input type="checkbox" checked={form.callback} onChange={e => update('callback', e.target.checked)} className="h-4 w-4 rounded border-navy/25 text-brand-orange focus:ring-brand-orange" data-id="element-104" data-mp="29nquf02ad43h" />
                {t.booking.callback}
              </label>

              {error && <p className="mt-4 flex items-center gap-2 rounded-lg bg-brand-orange/10 px-3 py-2.5 text-sm font-medium text-brand-orangeDark" data-id="element-105" data-mp="2scowql7r089c">
                  <AlertCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-106" data-mp="2ngzuub89af9v" />
                  {error}
                </p>}

              <button type="submit" disabled={status === 'submitting'} className="mt-6 w-full rounded-lg bg-brand-orange px-6 py-4 text-base font-semibold text-white transition-colors duration-150 hover:bg-brand-orangeDark disabled:opacity-70" data-id="element-107" data-mp="2xcjsp36dro19">
                {status === 'submitting' ? t.booking.submitting : t.booking.submit}
              </button>
            </form>}
        </div>
      </div>
    </section>;
}